import { safeError } from "../../lib/apiError";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  getConsent,
  isConsentTryAgain,
  postDecisions,
  toDecisions,
  WEB_DEVICE_TOPICS,
  type ConsentTopic,
  type TopicKey,
  type WebDeviceTopic,
} from "../../api/consent";
import { toConsentLocale } from "../../lib/consentLocale";
import { applyDeviceConsent } from "../../lib/deviceConsent";
import logger from "../../lib/logger";
import { Z_CONSENT_BANNER } from "../../lib/zLayers";
import { useConsentStore } from "../../store/consentStore";
import ConsentChecklist, { openConsentPage } from "./ConsentChecklist";

const isWebDeviceTopic = (t: ConsentTopic): t is ConsentTopic & { key: WebDeviceTopic } =>
  t.scope === "DEVICE" && (WEB_DEVICE_TOPICS as readonly string[]).includes(t.key);

/** All three answers carry the same weight (Figma 4435:3374: all Secondary, same size). */
const BUTTON_CLASS =
  "consent-banner-button h-10 w-full min-w-[144px] rounded-full border border-[var(--border)] bg-[var(--bg-surface)] px-4 text-sm font-semibold text-[var(--foreground)] hover:bg-[var(--bg-hover)] disabled:opacity-40";

/**
 * The storage banner (Figma "Web / Privacy / Storage banner / Over sign in" 4435:3374, Dark 4436:4484): at the bottom
 * over every route, signed in or not, while a web device item (crash reports, external media) has no answer for this
 * browser. Boxes start unticked; Accept selected · Accept all · Reject all send one batch (source FIRST_START).
 * Sentry starts only after crash_reports is ACCEPTED.
 */
export default function StorageBanner() {
  const { t, i18n } = useTranslation();
  const locale = toConsentLocale(i18n.language);
  const queryClient = useQueryClient();
  const setDevice = useConsentStore((s) => s.setDevice);
  const setPending = useConsentStore((s) => s.setPending);
  const clearDevice = useConsentStore((s) => s.clearDevice);
  // A gate dialog (date of birth, Before you start, re-consent) lists the same items: one consent screen at a time.
  const gateOpen = useConsentStore((s) => s.gateOpen);
  const [ticked, setTicked] = useState<Set<TopicKey>>(() => new Set());
  const [saving, setSaving] = useState(false);
  /** "retry": 503 / no network (nothing recorded, try again); "error": any other refusal. */
  const [failed, setFailed] = useState<null | "retry" | "error">(null);
  const [answered, setAnswered] = useState(false);

  const { data } = useQuery({
    queryKey: ["consent", locale],
    queryFn: () => getConsent(locale),
    staleTime: Infinity,
  });

  const deviceTopics = useMemo(() => (data?.topics ?? []).filter(isWebDeviceTopic), [data]);
  const rows = useMemo(() => deviceTopics.filter((x) => x.needsAnswer), [deviceTopics]);
  const privacyNotice = data?.topics.find((x) => x.key === "privacy_notice");

  // The server keeps this device's answers (X-Device-Id): copy the ones it counts into the local store, so a
  // cleared localStorage does not lose them, and drop a stored answer when the server says the topic needs an answer
  // again (a new version): an older ACCEPTED must not keep Sentry on. Then apply them (Sentry on/off).
  useEffect(() => {
    let changed = false;
    for (const x of deviceTopics) {
      if (x.needsAnswer) {
        if (useConsentStore.getState().device[x.key]) {
          clearDevice(x.key);
          changed = true;
        }
        continue;
      }
      if (!x.decision) continue;
      const have = useConsentStore.getState().device[x.key];
      if (have?.decision === x.decision && have.versionId === x.versionId) continue;
      setDevice(x.key, { versionId: x.versionId, decision: x.decision });
      changed = true;
    }
    if (changed) applyDeviceConsent();
  }, [deviceTopics, setDevice, clearDevice]);

  if (answered || gateOpen || rows.length === 0) return null;

  function toggle(key: TopicKey) {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function answer(accepted: Set<TopicKey>) {
    setSaving(true);
    setFailed(null);
    try {
      const out = await postDecisions(toDecisions(rows, accepted, "FIRST_START"));
      for (const x of rows) {
        setDevice(x.key, {
          versionId: x.versionId,
          decision: accepted.has(x.key) ? "ACCEPTED" : "DECLINED",
        });
      }
      setPending(out.consentPending);
      applyDeviceConsent();
      setAnswered(true);
      void queryClient.invalidateQueries({ queryKey: ["consent"] });
    } catch (e) {
      // 503 = the server could not read the legal site's commit and recorded nothing: never treated as accepted.
      logger.warn("[Consent] storage banner answer failed", safeError(e));
      setFailed(isConsentTryAgain(e) ? "retry" : "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      role="region"
      aria-label={t("consent_banner_title")}
      data-testid="consent-banner"
      className="fixed inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto border-t border-[var(--border)] bg-[var(--bg-surface)] shadow-[0_-4px_16px_rgba(15,23,42,0.08)]"
      style={{ zIndex: Z_CONSENT_BANNER }}
    >
      <div className="mx-auto flex max-w-[1280px] flex-wrap items-start gap-x-8 gap-y-4 px-4 py-4 md:px-8">
        <div className="min-w-[260px] flex-[1_1_360px]">
          <h2 className="m-0 text-base font-semibold text-[var(--foreground)]">
            {t("consent_banner_title")}
          </h2>
          <p className="mt-1 mb-1 text-sm text-[var(--foreground-muted)]">{t("consent_banner_text")}</p>
          {privacyNotice?.url && (
            <button
              type="button"
              data-testid="consent-read-privacy-notice"
              onClick={() => openConsentPage(privacyNotice)}
              className="text-sm font-semibold text-[var(--primary-text)] underline"
            >
              {t("consent_read_privacy_notice")}
            </button>
          )}
          {failed && (
            <p role="alert" data-testid="consent-banner-error" className="mt-2 text-sm text-[var(--destructive-text)]">
              {failed === "retry" ? t("consent_try_again") : t("consent_error")}
            </p>
          )}
        </div>
        <div className="min-w-[260px] flex-[1_1_320px]">
          <ConsentChecklist topics={rows} ticked={ticked} onToggle={toggle} disabled={saving} />
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-[0_0_180px]">
          <button
            type="button"
            data-testid="consent-accept-selected"
            className={BUTTON_CLASS}
            disabled={saving}
            onClick={() => answer(new Set(ticked))}
          >
            {t("consent_accept_selected")}
          </button>
          <button
            type="button"
            data-testid="consent-accept-all"
            className={BUTTON_CLASS}
            disabled={saving}
            onClick={() => answer(new Set(rows.map((x) => x.key)))}
          >
            {t("consent_accept_all")}
          </button>
          <button
            type="button"
            data-testid="consent-reject-all"
            className={BUTTON_CLASS}
            disabled={saving}
            onClick={() => answer(new Set())}
          >
            {t("consent_reject_all")}
          </button>
        </div>
      </div>
    </section>
  );
}
